import clsx from 'clsx';
import {
  createContext,
  memo,
  type ComponentType,
  type PropsWithChildren,
  type ReactNode,
  useContext,
} from 'react';

import { Layout } from '../Layout/Layout';
import { PageHeader } from './subcomponents/PageHeader';
import { PageLayoutProps } from './types';
import './PageLayout.css';

interface PageLayoutHostProviderProps {
  children: ReactNode;
  headerSlot: ComponentType<PropsWithChildren>;
}

const PageLayoutHeaderSlotContext =
  createContext<ComponentType<PropsWithChildren> | null>(null);

export const PageLayoutHostProvider = memo(function PageLayoutHostProvider({
  children,
  headerSlot,
}: PageLayoutHostProviderProps) {
  return (
    <PageLayoutHeaderSlotContext.Provider value={headerSlot}>
      {children}
    </PageLayoutHeaderSlotContext.Provider>
  );
});

export const PageLayout = memo(
  ({
    children,
    title,
    actions,
    bg = 'base',
    moreActions,
    statusBadge,
    subtitle,
    trailingSlot,
  }: PageLayoutProps) => {
    const HeaderSlot = useContext(PageLayoutHeaderSlotContext);
    const header = (
      <PageHeader
        actions={actions}
        moreActions={moreActions}
        statusBadge={statusBadge}
        subtitle={subtitle}
        title={title}
        trailingSlot={trailingSlot}
      />
    );

    return (
      <Layout
        className={clsx('eds-page-layout', {
          'eds-page-layout--hosted': HeaderSlot,
        })}
        direction="column"
        flex="1"
        spacing="none"
      >
        {HeaderSlot ? <HeaderSlot>{header}</HeaderSlot> : header}
        <Layout
          className={clsx(
            'eds-page-layout--content',
            `eds-page-layout--bg-${bg}`,
          )}
          direction="column"
          flex="1"
        >
          {children}
        </Layout>
      </Layout>
    );
  },
);

PageLayout.displayName = 'PageLayout';
